/**
 * Inventory Pending Stock Transfers Exception Config
 *
 * Covers: ex-inv-ls-pending-transfers
 *
 * Outlet-to-outlet stock transfers still in motion (not received or cancelled).
 * Excludes same-day transfers and deleted records.
 */
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import {
  consignmentReferenceSortValue,
  createConsignmentLinkCell,
} from './shared/consignmentRenderers'
import { createDaysValueRenderer } from './shared/daysRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const REFERENCE_FROZEN_WIDTH = 220

const renderDaysValue = createDaysValueRenderer({ redAtDays: 14, orangeAtDays: 7 })

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Reference',
    width: REFERENCE_FROZEN_WIDTH,
    minWidth: REFERENCE_FROZEN_WIDTH,
    freeze: 'left',
    render: createConsignmentLinkCell('Transfer'),
    link: { field: 'lightspeed_link', system: 'lightspeed' },
    getSortValue: consignmentReferenceSortValue,
  },
  {
    key: 'created_at_ls',
    label: 'Created',
    render: (value, _row, ctx) => ctx.variant === 'inspector' && value ? formatDate(value) : (value || '-'),
    exportFormat: 'date',
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (value) => value || '-',
  },
  {
    key: 'status',
    label: 'Transfer Status',
    render: (value) => value || '-',
  },
  {
    key: 'days_value',
    label: 'Age',
    align: 'right',
    render: renderDaysValue,
    getSortValue: (row) => Number(row.days_value) || 0,
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const pendingTransfersConfig: ExceptionConfig = {
  codes: ['ex-inv-ls-pending-transfers'],
  columns,
  defaultSortColumn: 'created_at_ls',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowTitle: (row) =>
    row.reference_number
      ? `Transfer ${row.reference_number}`
      : `Transfer ${String(row.ls_id ?? '').slice(0, 8)}`,
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      row.created_at_ls ? `created ${datePart(row.created_at_ls)}` : null,
      typeof row.days_value === 'number' && row.days_value > 0
        ? `${row.days_value} days pending`
        : null,
      moneyPart(row.total_cost),
    ]),
}
